/* eslint-disable no-console */
import {PlusOutlined, SearchOutlined} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  Popconfirm,
  Radio,
  Select,
  Table,
  TableColumnsType,
  Tag,
  Typography
} from 'antd';
import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {ReactComponent as Delete} from '../../../../../assets/icons/deleteIcon.svg';
import {ReactComponent as Edit} from '../../../../../assets/icons/editIcon.svg';
import BlockAccessPage from '../../../../../components/BlockAccess/BlockAccessPage';
import ExternalStakeholderLimitModal from '../../../../../components/BlockAccess/ExternalStakeholderLimitModal';
import {objTypes} from '../../../../../components/DrawerForm/Constants/commonConstants';
import DrawerForm from '../../../../../components/DrawerForm/Utils/DrawerForm';
import {
  accessKeyLabel,
  permissionKeyLabel,
  roleKeyLabel,
  stackholderKeyLabel
} from '../../../../../components/Forms/Constants/IP/commonKeyLabel';
import Loader from '../../../../../components/Loader/Loader';
import UpgradeButton from '../../../../../components/UpgradeButton/UpgradeButton';
import {
  blockAccessTypes,
  blockModelMessages
} from '../../../../../constants/blockPageConstatnts';
import {Routes} from '../../../../../constants/Routes';
import accessTypeApi from '../../../../../services/modules/JSON/accessTypes';
import roleTypeApi from '../../../../../services/modules/JSON/roleTypes';
import stackHolderTypeJson from '../../../../../services/modules/JSON/stackHolderTypes';
import allUserDetails from '../../../../../services/modules/User/allUserDetails';
import getTotalUserCount from '../../../../../services/modules/User/totalUserCount';
import getUserGroups from '../../../../../services/modules/User/userGroupDetails';
import assignPermissionExternal from '../../../../../services/modules/UserManager/assignPermissionsExternal';
import assignRolePermission from '../../../../../services/modules/UserManager/assignRolePermission';
import deleteUser from '../../../../../services/modules/UserManager/deleteUser';
import editUserApi from '../../../../../services/modules/UserManager/editUserApi';
import {
  accessModule,
  countModule
} from '../../../../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../../../../store/platformStore/platformStore';
import useJSONStore from '../../../../../store/StaticJson/jsonStore';
import useUserStore from '../../../../../store/userStore/userStore';
import {yearSelect} from '../../../../../utils/FormUtils/yearSelect';
import showMessage from '../../../../../utils/showMessage';
import {
  getModuleDataLimit,
  getSubscriptionPlanType,
  hasMaxLimitReached,
  shouldBlockReadOnly,
  shouldBlockModuleAccess
} from '../../../../../utils/SubscriptionHelper';
import {ColumnDataType} from '../Constants/ColumnDataType';

import AddUserDrawerForm from './AddUserDrawerForm';
import EditUserDrawerForm from './EditUserDrawerForm';

export interface PostFormData {
  users?: any[];
  roles?: string;
  permissions?: string[];
  selectedStackHolder?: string;
  selectedAccess?: string | null;
  selectedUserGroup?: string;
  addMemberForms?: FormType[];
  selectedAdmin?: string;
  user?: string;
}

export interface FormType {
  id: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
}

export interface Filters {
  search?: string;
  access?: string;
  stackholderType?: string;
  memberType?: string;
  userGroupId?: string[];
}
export interface UserGroupType {
  name: string;
  _id: string;
}
export interface EditUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  auth0UserId: string;
  stakeHolderType: string;
  role: string;
  type: string;
  permissionCount: number;
  roleId: string;
  accessId: string;
  permissions: string[];
}

const UserManager: React.FC = () => {
  const [dataSource, setDataSource] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(false);
  const [isUserGroupLoading, setIsUserGroupLoading] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [userGroups, setUserGroups] = useState<UserGroupType[]>([]);
  const [postFormData, setPostFormData] = useState<any>({});
  const [openMaxExternalLimit, setOpenMaxExternalLimit] = useState(false);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
  }>({
    page: 1,
    pageSize: 10
  });
  const [paginationTotal, setPaginationTotal] = useState<number>(10);

  const [filters, setFilters] = useState<Filters>({
    stackholderType: 'Internal'
  });

  const navigate = useNavigate();

  const stackHolderType = useJSONStore.getState().stackHolderTypes;
  const accessTypes = useJSONStore.getState().accessTypes;
  const userDetails = useUserStore.getState().userDetails;

  const UserFormKeyLabel = [
    {
      key: stackholderKeyLabel.key,
      label: stackholderKeyLabel.label,
      type: objTypes.string
    },
    {
      key: roleKeyLabel.key,
      label: roleKeyLabel.label,
      type: objTypes.string
    },
    {
      key: accessKeyLabel.key,
      label: accessKeyLabel.label,
      type: objTypes.string
    },
    {
      key: permissionKeyLabel.key,
      label: permissionKeyLabel.label
    }
  ];

  const UserFormKeyLabelEdit = [
    {
      key: roleKeyLabel.key,
      label: roleKeyLabel.label,
      type: objTypes.string
    },
    {
      key: accessKeyLabel.key,
      label: accessKeyLabel.label,
      type: objTypes.string
    },
    {
      key: permissionKeyLabel.key,
      label: permissionKeyLabel.label
    }
  ];

  const handlePostDataChange = (data: Partial<any>) => {
    if (data.selectedStackHolder) {
      setPostFormData(() => {
        return {
          ...data,
          selectedStackHolder: data.selectedStackHolder,
          users: [],
          permissions: []
        };
      });
    } else {
      setPostFormData((prevData: any) => ({...prevData, ...data}));
    }
  };

  const handleEditDataChange = (data: Partial<any>) => {
    setPostFormData((prevData: any) => ({...prevData, ...data}));
  };

  const handleAssignPermissions = async (): Promise<any> => {
    try {
      const currentYear = yearSelect()?.[0].value;

      if (postFormData.selectedStackHolder === 'Internal') {
        const res = await assignRolePermission(postFormData, currentYear);

        return res;
      }
      if (postFormData.selectedStackHolder === 'External') {
        const res = await assignPermissionExternal(postFormData);

        return res;
      }
    } catch (error) {}
  };

  const handleEditUser = async () => {
    try {
      const res = await editUserApi(postFormData);

      return res;
    } catch (error) {}
  };

  useEffect(() => {
    setIsLoading(true);
    setIsDrawerOpen(false);
    setIsEditOpen(false);
    Promise.all([
      // allUserDetails(filters),
      getUserGroups('Internal'),
      stackHolderTypeJson(),
      roleTypeApi(),
      accessTypeApi(),
      getTotalUserCount()
    ])
      .then(([res1]) => {
        // setDataSource(res1 ?? []);
        setUserGroups(res1 ?? []);
      })
      .catch(() => {
        // setDataSource([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsTableDataLoading(true);
      Promise.all([
        allUserDetails({...filters, ...pagination}),
        getTotalUserCount()
      ])
        .then(([res1]) => {
          setDataSource(res1?.users ?? []);
          setPaginationTotal(res1?.total ?? 0);
          // setPagination((prev) => {
          //   return {...prev, total: res1?.total ?? 0};
          // });
        })
        .catch(() => {})
        .finally(() => {
          setIsTableDataLoading(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const prevFilters = useRef(filters);

  useEffect(() => {
    setIsTableDataLoading(true);
    if (filters.stackholderType !== prevFilters.current.stackholderType) {
      if (
        filters.stackholderType === 'External' &&
        shouldBlockModuleAccess(accessModule.externalStakeholders)
      ) {
        setIsTableDataLoading(false);
        setIsUserGroupLoading(false);

        return;
      }
      setIsUserGroupLoading(true);
      Promise.all([
        allUserDetails({...filters, ...pagination}),
        getUserGroups(filters.stackholderType ?? 'Internal')
      ])
        .then(([res1, res2]) => {
          setDataSource(res1?.users ?? []);
          setUserGroups(res2);
          setPaginationTotal(res1?.total ?? 0);

          // setPagination((prev) => {
          //   return {...prev, total: res1?.total ?? 0};
          // });
        })
        .catch(() => {
          // setDataSource([]);
          setUserGroups([]);
        })
        .finally(() => {
          setIsTableDataLoading(false);
          setIsUserGroupLoading(false);
        });
    } else {
      allUserDetails({...filters, ...pagination})
        .then((res1) => {
          setDataSource(res1?.users ?? []);
          setPaginationTotal(res1?.total ?? 0);

          // setPagination((prev) => {
          //   return {...prev, total: res1?.total ?? 0};
          // });
        })
        .catch(() => {
          // setDataSource([]);
        })
        .finally(() => {
          setIsTableDataLoading(false);
        });
    }

    prevFilters.current = filters;
  }, [pagination]);

  let debounceTimer: NodeJS.Timeout;

  const handleChangeFilter = (
    key: keyof Filters,
    value: string,
    shouldClear: boolean = false
  ) => {
    if (!shouldClear) {
      if (key === 'search') {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value
          }));
          setPagination((prev) => {
            return {...prev, page: 1};
          });
        }, 500);
      } else {
        setFilters((prevFilters) => ({
          ...prevFilters,
          [key]: value
        }));
        setPagination((prev) => {
          return {...prev, page: 1};
        });
      }
    } else {
      setFilters(() => ({
        [key]: value
      }));
      setPagination((prev) => {
        return {...prev, page: 1};
      });
    }
  };

  const handleCheckboxChange = (value: string, checked: boolean) => {
    setFilters((prevFilters) => {
      const currentGroup = prevFilters.userGroupId ?? [];
      const updatedGroup = checked
        ? [...currentGroup, value]
        : currentGroup.filter((item) => item !== value);

      return {
        ...prevFilters,
        userGroupId: updatedGroup
      };
    });
    setPagination((prev) => {
      return {...prev, page: 1};
    });
  };

  const columns: TableColumnsType<ColumnDataType> = [
    {
      title: 'Name',
      dataIndex: ['firstName', 'lastName'],
      key: '_id',
      render: (value: string, record: ColumnDataType) => {
        return <div>{`${record.firstName} ${record.lastName}`}</div>;
      }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value: string) => <div>{value}</div>
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (value: string) => <div>{value || 'Example Role'}</div>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (value: string) => (
        <div>{value.split(':')[1] || 'Example Type'}</div>
      )
    },
    {
      title: 'Permission Count',
      dataIndex: 'permissionCount',
      key: 'permissionCount',
      render: (value: string) => (
        <Tag
          style={{
            borderRadius: '35%',
            background: '#D2D4DA',
            padding: '3px 7px',
            fontWeight: 500
          }}
        >
          {value}
        </Tag>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'action',
      width: '20%',
      render: (value: string, record: ColumnDataType) => {
        return record?.isCompanyAdmin === true ||
          (record?.refUserId?.toString() === userDetails?.refUserId &&
            userDetails?.refUserId) ? (
          <></>
        ) : shouldBlockReadOnly() ? (
          <Flex
            align="center"
            justify="flex-start"
            style={{width: 'fit-content'}}
          >
            <Button
              onClick={() => {
                usePlatformStore
                  .getState()
                  .setOpenOnFreeTrialMessageModel(true);
              }}
              shape="circle"
              type="link"
              style={{marginRight: '20px', backgroundColor: '#F5FBF3'}}
              icon={<Edit />}
            />
            <Button
              shape="circle"
              type="link"
              onClick={() => {
                usePlatformStore
                  .getState()
                  .setOpenOnFreeTrialMessageModel(true);
              }}
              style={{marginRight: '20px', backgroundColor: '#F5FBF3'}}
              icon={<Delete />}
            />
          </Flex>
        ) : (
          <Flex
            align="center"
            justify="flex-start"
            style={{width: 'fit-content'}}
          >
            <Button
              onClick={() => {
                handleEditClick(record);
              }}
              shape="circle"
              type="link"
              style={{marginRight: '20px', backgroundColor: '#F5FBF3'}}
              icon={<Edit />}
            />

            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => {
                setIsTableDataLoading(true);
                handleDelete(record);
              }}
            >
              <Button
                shape="circle"
                type="link"
                style={{marginRight: '20px', backgroundColor: '#F5FBF3'}}
                icon={<Delete />}
              />
            </Popconfirm>
          </Flex>
        );
      },
      key: '_id'
    }
  ];

  const handleDelete = (recordDeleteId: ColumnDataType) => {
    deleteUser(recordDeleteId._id)
      .then((res: any) => {
        showMessage(res);
      })
      .catch(() => {})
      .finally(() => {
        setIsTableDataLoading(false);
        setRefresh(true);
      });
  };

  const handleEditClick = (record: ColumnDataType) => {
    const access = accessTypes.find((item) => item.id === record.accessId)
      ?.description;
    setPostFormData({
      roles: record.role,
      selectedStackHolder: filters.stackholderType,
      selectedAccess: access,
      permissions: record.permissions,
      user: record.auth0UserId,
      Role: record.role,
      Access: access
    });
    setIsEditOpen(true);
  };

  const pageBlocker = () => {
    if (filters?.stackholderType !== 'External') {
      if (shouldBlockModuleAccess(accessModule.internalUsers)) {
        return (
          <div style={{marginBottom: 50}}>
            <BlockAccessPage blockedType={blockAccessTypes.PARTIAL} />
          </div>
        );
      }

      if (hasMaxLimitReached(countModule.internalUsers)) {
        return (
          <div style={{marginBottom: 50}}>
            <BlockAccessPage
              blockedType={blockAccessTypes.PARTIAL}
              messageTitle={blockModelMessages.user.title.limitReached}
              messageDescription={blockModelMessages.user.description.limitReached(
                getSubscriptionPlanType(),
                getModuleDataLimit(accessModule.internalUsers)
              )}
            />
          </div>
        );
      }
    } else {
      if (shouldBlockModuleAccess(accessModule.externalStakeholders)) {
        return (
          <div style={{marginBottom: 50}}>
            <BlockAccessPage blockedType={blockAccessTypes.PARTIAL} />
          </div>
        );
      }

      if (hasMaxLimitReached(countModule.externalStakeholders)) {
        return (
          <div style={{marginBottom: 50}}>
            <BlockAccessPage
              blockedType={blockAccessTypes.PARTIAL}
              messageTitle={blockModelMessages.user.title.limitReached}
              messageDescription={blockModelMessages.user.description.limitReached(
                getSubscriptionPlanType(),
                getModuleDataLimit(accessModule.externalStakeholders)
              )}
            />
          </div>
        );
      }
    }
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <Flex className="usermFlex">
        <Flex align="center" justify="space-between">
          <Typography.Title level={4}>User Management</Typography.Title>
          {(hasMaxLimitReached(countModule.externalStakeholders) ||
            shouldBlockModuleAccess(accessModule.externalStakeholders)) &&
          hasMaxLimitReached(countModule.internalUsers) ? (
            <UpgradeButton title="Add" />
          ) : (
            <Button
              id="internalButton"
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => {
                setIsDrawerOpen(true);
              }}
            >
              Add
            </Button>
          )}
        </Flex>
        <Divider />
        <Flex className="content" align="center" justify="center">
          <Flex
            align="center"
            justify="space-between"
            style={{width: '100%', marginBottom: '20px'}}
          >
            {(filters.stackholderType === 'External' &&
              shouldBlockModuleAccess(accessModule.externalStakeholders)) ||
            shouldBlockReadOnly() ? (
              <div style={{width: '295px', padding: '10px'}}></div>
            ) : (
              <Input
                placeholder="Search"
                style={{width: '295px', padding: '10px'}}
                suffix={
                  <SearchOutlined
                    style={{fontSize: '20px', color: '#9496A1'}}
                  />
                }
                onChange={(e) => {
                  handleChangeFilter('search', e.target.value);
                }}
                disabled={isTableDataLoading}
              />
            )}
            <Flex gap="10px">
              {(filters.stackholderType === 'External' &&
                shouldBlockModuleAccess(accessModule.externalStakeholders)) ||
              shouldBlockReadOnly() ? (
                <div style={{width: '295px', padding: '10px'}}></div>
              ) : (
                <Select
                  showSearch
                  allowClear
                  placeholder="Access Type"
                  style={{width: 160}}
                  onChange={(value) => {
                    handleChangeFilter('access', value);
                  }}
                  options={accessTypes?.map((accessType) => ({
                    id: accessType.id,
                    value: accessType.name,
                    label: accessType.description
                  }))}
                  disabled={isTableDataLoading}
                />
              )}
              <Select
                placeholder="Stackholder Type"
                showSearch
                style={{width: 230}}
                onChange={(value) => {
                  handleChangeFilter('stackholderType', value, true);
                }}
                options={stackHolderType}
                value={filters?.stackholderType}
                disabled={isTableDataLoading}
              />
            </Flex>
          </Flex>
          <Flex
            justify="center"
            gap="0.5rem"
            style={
              (filters.stackholderType === 'External' &&
                shouldBlockModuleAccess(accessModule.externalStakeholders)) ||
              shouldBlockReadOnly()
                ? {display: 'none'}
                : {alignSelf: 'end', marginBottom: '10px'}
            }
          >
            {isUserGroupLoading ? (
              <div style={{height: '10px'}}></div>
            ) : (
              <>
                {(filters.stackholderType === 'Internal' ||
                  !filters.stackholderType) && (
                  <Radio.Group
                    value={filters.memberType}
                    onChange={(e) => {
                      handleChangeFilter('memberType', e.target.value);
                    }}
                    disabled={isTableDataLoading}
                  >
                    <Radio value="Admin">Admin</Radio>
                    <Radio value="User">User</Radio>
                  </Radio.Group>
                )}
                {userGroups?.map((userGroup: UserGroupType) => (
                  <Checkbox
                    key={userGroup._id}
                    value={userGroup._id}
                    checked={filters.userGroupId?.includes(userGroup._id)}
                    onChange={(e) => {
                      handleCheckboxChange(userGroup._id, e.target.checked);
                    }}
                    disabled={isTableDataLoading}
                  >
                    {userGroup.name}
                  </Checkbox>
                ))}
              </>
            )}
          </Flex>
          {filters.stackholderType === 'External' &&
          shouldBlockModuleAccess(accessModule.externalStakeholders) ? (
            <BlockAccessPage
              style={{
                width: '100%',
                height: '100%',
                backdropFilter: 'blur(8px)',
                minHeight: '600px'
              }}
            />
          ) : (
            <>
              <Table
                columns={columns}
                dataSource={dataSource}
                className="table"
                rowKey="_id"
                loading={{
                  spinning: isTableDataLoading,
                  indicator: <Loader />
                }}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: 10,
                  current: pagination.page,
                  pageSize: pagination.pageSize,
                  total: paginationTotal,
                  onChange: (page, pageSize) => {
                    setPagination((prev) => {
                      return {...prev, page, pageSize};
                    });
                  }
                }}
              />
              {filters?.stackholderType && pageBlocker()}
            </>
          )}
        </Flex>
      </Flex>
      <DrawerForm
        open={isDrawerOpen}
        onCancel={() => {
          setIsDrawerOpen(false);
          setPostFormData({});
        }}
        setRefresh={setRefresh}
        FormComponent={
          <AddUserDrawerForm
            key={postFormData.selectedStackHolder}
            postFormData={postFormData}
            onPostDataChange={handlePostDataChange}
          />
        }
        API={handleAssignPermissions}
        stepsTitle={['Preferences', 'Select Stakeholder']}
        formPreview={UserFormKeyLabel}
        isConfigurator={true}
      />

      <DrawerForm
        open={isEditOpen}
        onCancel={() => {
          setIsEditOpen(false);
          setPostFormData({});
        }}
        setRefresh={setRefresh}
        FormComponent={
          <EditUserDrawerForm
            key={postFormData.selectedStackHolder}
            postFormData={postFormData}
            onPostDataChange={handleEditDataChange}
          />
        }
        API={handleEditUser}
        stepsTitle={['Edit Preferences']}
        formPreview={UserFormKeyLabelEdit}
        isConfigurator={true}
        neglectReset={true}
        UMData={postFormData}
      />
      <ExternalStakeholderLimitModal
        maxLimit={getModuleDataLimit(accessModule.externalStakeholders)}
        open={openMaxExternalLimit}
        onUpgrade={() => {
          navigate(Routes.PRICING_DETAILS);
          setOpenMaxExternalLimit(false);
        }}
        onCancel={() => {
          setOpenMaxExternalLimit(false);
        }}
      ></ExternalStakeholderLimitModal>
    </>
  );
};

export default UserManager;
